import React, { useRef } from 'react';
import { ELSButton } from '@els/els-component-button-react';
import { FlexLayout } from '../flex/FlexLayout.component';
import { FlexItem } from '../flex/FlexItem.component';
import { FlexLayoutModifier } from '../flex/flex.constants';
import { ELSButtonSize, ELSButtonType } from '../../models/button.models';

export type ShuffleBarProps = {
  // Shown at the right, such as the page's close button, so both stay locked together above the content
  headerEnd?: React.ReactNode;
  // The shuffle controls, usually ShuffleOrderButtons
  children: React.ReactNode;
};

// The row above a library panel's content (Quiz questions, Flashcards) holding its shuffle controls
export const ShuffleBar = (props: ShuffleBarProps): JSX.Element => (
  <div className="c-naa-panel__shuffle-bar">
    <FlexLayout modifiers={[FlexLayoutModifier.GUTTERS, FlexLayoutModifier.MIDDLE]}>
      <FlexItem modifiers={[FlexLayoutModifier.GROW]}>
        {props.children}
      </FlexItem>
      {props.headerEnd && (
        <FlexItem>
          {props.headerEnd}
        </FlexItem>
      )}
    </FlexLayout>
  </div>
);

export type ShuffleOrderButtonsProps = {
  isShuffled: boolean;
  isDisabled: boolean;
  onShuffle: () => void;
  onReset: () => void;
};

// Shuffle, and once shuffled Reset order, for items the panel has already fetched (see useShuffledOrder)
export const ShuffleOrderButtons = (props: ShuffleOrderButtonsProps): JSX.Element => {
  const shuffleButtonRef = useRef<HTMLButtonElement>(null);

  // Reset order is removed once the order is back, so focus moves to Shuffle rather than falling to the page
  const handleReset = () => {
    shuffleButtonRef.current?.focus();
    props.onReset();
  };

  return (
    <FlexLayout modifiers={[FlexLayoutModifier.GUTTERS, FlexLayoutModifier.MIDDLE, FlexLayoutModifier.LEFT]}>
      <FlexItem>
        <ELSButton
          preventAutoPending
          type={ELSButtonType.SECONDARY}
          size={ELSButtonSize.SMALL}
          isDisabled={props.isDisabled}
          onClick={props.onShuffle}
          // ELSButton takes no ref, but spreads nativeProps onto its <button>
          nativeProps={{ ref: shuffleButtonRef }}
        >
          Shuffle
        </ELSButton>
      </FlexItem>
      {props.isShuffled && (
        <FlexItem>
          <ELSButton preventAutoPending type={ELSButtonType.LINK} onClick={handleReset}>
            Reset order
          </ELSButton>
        </FlexItem>
      )}
    </FlexLayout>
  );
};
